import React from 'react';
import { ExternalLink, MapPin, ChevronDown, Calculator } from 'lucide-react';
import { PrimeIndemnite } from '../../types';
import { CTA_LINKS } from '../../constants/ctaLinks';
import PrimeFormula from './prime-details/PrimeFormula';
import PrimeValues from './prime-details/PrimeValues';
import PrimeCalculationExample from './prime-details/PrimeCalculationExample';

interface PrimeDetailsProps {
  prime: PrimeIndemnite;
  currentIndiceMajore?: number | null;
  selectedZone?: number | null;
  documentationUrl?: string;
  professionId?: string;
  showCalculationDetails?: boolean;
  onToggleCalculationDetails?: () => void;
}

export default function PrimeDetails({
  prime,
  currentIndiceMajore,
  selectedZone,
  documentationUrl,
  professionId = 'infirmier',
  showCalculationDetails = false,
  onToggleCalculationDetails
}: PrimeDetailsProps) {
  const Icon = prime.icon;
  
  const guyaneOpportunitiesLink = professionId === 'infirmier' 
    ? CTA_LINKS.INFIRMIER.GUYANE_OPPORTUNITIES 
    : CTA_LINKS.GENERAL.MAIN_WEBSITE;

  const renderPrimeDetails = () => {
    if (!prime.calculable || !prime.calculDetails) return null;
    if (!showCalculationDetails) return null;

    console.log('PrimeDetails - Rendering details for:', prime.id, 'Current Indice Majore:', currentIndiceMajore, 'Prime Montant Utilisateur:', prime.montantUtilisateur, 'Calculable:', prime.calculable, 'CalculDetails:', prime.calculDetails);

    switch (prime.id) {
      case 'segur':
        return (
          <div className="space-y-4">
            <PrimeCalculationExample
              title="Calcul du CTI Ségur"
              color="green"
              steps={[
                { 
                  label: 'Formule', 
                  value: '49 points d\'indice majoré × Valeur du point d\'indice'
                },
                { 
                  label: 'Points CTI', 
                  value: '49 points'
                },
                { 
                  label: 'Valeur du point', 
                  value: '4,92278 €',
                  calculation: 'Valeur officielle 2024'
                }
              ]}
              result={{
                label: 'CTI Ségur mensuel',
                value: `${prime.montantUtilisateur.toFixed(2)}€`
              }}
            />
            
           
          </div>
        );

      case 'iss':
        console.log('PrimeDetails - ISS case reached. currentIndiceMajore:', currentIndiceMajore);
        return (
          <div className="space-y-4">
            {prime.issConfig && currentIndiceMajore && (
              <PrimeCalculationExample
                title="Calcul de l'ISS (Prime des 13 heures)"
                color="green"
                steps={[
                  { 
                    label: 'Formule', 
                    value: '(Indice majoré × 4,6860 € × 12) × 13/1900'
                  },
                  { 
                    label: 'Indice majoré historique (2021)', 
                    value: `${prime.issConfig.indiceMajoreHistorique}`,
                    calculation: 'Votre échelon en 2021'
                  },
                  { 
                    label: 'Valeur du point (2021)', 
                    value: '4,6860 €'
                  },
                  { 
                    label: 'Traitement annuel 2021', 
                    value: `${(prime.issConfig.indiceMajoreHistorique * 4.6860 * 12).toFixed(2)}€`,
                    calculation: `${prime.issConfig.indiceMajoreHistorique} × 4,6860 € × 12`
                  },
                  { 
                    label: 'Facteur ISS', 
                    value: '13/1900'
                  }
                ]}
                result={{
                  label: 'ISS mensuelle',
                  value: `${prime.montantUtilisateur.toFixed(2)}€`
                }}
              />
            )}
          </div>
        );

      case 'pss':
        return (
          <div className="space-y-4">
            {currentIndiceMajore && (
              <PrimeCalculationExample
                title={`Calcul de la PSS pour IM ${currentIndiceMajore}`}
                color="teal"
                steps={[
                  { 
                    label: 'Formule', 
                    value: 'Indice majoré × 5 907,34 € / 1 200 × 10%'
                  },
                  { 
                    label: 'Traitement mensuel', 
                    value: `${((currentIndiceMajore * 5907.34) / 1200).toFixed(2)}€`,
                    calculation: `${currentIndiceMajore} × 5 907,34 € / 1 200`
                  },
                  { 
                    label: 'Taux PSS', 
                    value: '10%'
                  }
                ]}
                result={{
                  label: 'PSS mensuelle',
                  value: `${prime.montantUtilisateur.toFixed(2)}€`
                }}
              />
            )}
          </div>
        );

      case 'transport':
        return (
          <div className="space-y-4">
            <PrimeCalculationExample
              title="Calcul de l'indemnité transport"
              color="teal"
              steps={[
                { 
                  label: 'Formule', 
                  value: 'Montant de base × Taux de remboursement'
                },
                { 
                  label: 'Montant de base', 
                  value: `${prime.montantBase || 40}€`
                },
                { 
                  label: 'Taux de remboursement', 
                  value: `${prime.pourcentageRemboursement || 75}%`
                }
              ]}
              result={{
                label: 'Remboursement mensuel',
                value: `${prime.montantUtilisateur.toFixed(2)}€`
              }}
            />
          </div>
        );

      case 'indemnite_residence':
        return (
          <div className="space-y-4">
            {currentIndiceMajore && selectedZone && (
              <PrimeCalculationExample
                title={`Calcul de l'IR - Zone ${selectedZone}`}
                color="cyan"
                steps={[
                  { 
                    label: 'Formule', 
                    value: 'Traitement indiciaire annuel × Taux de zone / 12'
                  },
                  { 
                    label: 'Traitement annuel', 
                    value: `${((currentIndiceMajore * 5907.34) / 100).toFixed(2)}€`,
                    calculation: `${currentIndiceMajore} × 59,07€`
                  },
                  { 
                    label: `Taux zone ${selectedZone}`, 
                    value: selectedZone === 1 ? '3%' : selectedZone === 2 ? '1%' : '0%'
                  }
                ]}
                result={{
                  label: 'IR mensuelle',
                  value: `${prime.montantUtilisateur.toFixed(2)}€`
                }}
              />
            )}
          </div>
        );

      case 'iftd':
        return (
          <div className="space-y-4">
            {prime.iftdConfig && (prime.iftdConfig.dimanchesParMois > 0 || prime.iftdConfig.joursFeriesTravailles.length > 0) && (
              <PrimeCalculationExample
                title="Calcul de l'IFTD"
                color="teal"
                steps={[
                  { 
                    label: 'Formule', 
                    value: '60€ × Nb dimanches/mois + (60€ × Nb jours fériés/an ÷ 12)'
                  },
                  { 
                    label: 'Montant forfaitaire', 
                    value: '60€ par jour (8h de travail effectif)'
                  },
                  { 
                    label: 'Dimanches travaillés', 
                    value: `${prime.iftdConfig.dimanchesParMois} × 60€ = ${prime.iftdConfig.dimanchesParMois * 60}€`
                  },
                  { 
                    label: 'Jours fériés travaillés', 
                    value: `${prime.iftdConfig.joursFeriesTravailles.length} × 60€ ÷ 12 = ${((prime.iftdConfig.joursFeriesTravailles.length * 60) / 12).toFixed(2)}€`
                  }
                ]}
                result={{
                  label: 'IFTD mensuelle',
                  value: `${prime.montantUtilisateur.toFixed(2)}€`
                }}
              />
            )}
          </div>
        );

      case 'contagion':
        return (
          <div className="space-y-4">
            <PrimeCalculationExample
              title="Calcul de la prime de contagion"
              color="red"
              steps={[
                { 
                  label: 'Formule', 
                  value: '0,33€ × 2 × Nombre de jours travaillés par an ÷ 12'
                },
                { 
                  label: 'Montant par demi-journée', 
                  value: '0,33€'
                },
                { 
                  label: 'Montant par jour complet', 
                  value: '0,66€',
                  calculation: '2 demi-journées'
                },
                { 
                  label: 'Jours travaillés par an', 
                  value: `${prime.contagionConfig?.joursTravaillesParAn || 228} jours`
                }
              ]}
              result={{
                label: 'Prime de contagion mensuelle',
                value: `${prime.montantUtilisateur.toFixed(2)}€`
              }}
            />
          </div>
        );

      case 'prime_nuit':
        return (
          <div className="space-y-4">
            {prime.primeNuitConfig && currentIndiceMajore && (
              <PrimeCalculationExample
                title="Calcul de la prime de nuit"
                color="teal"
                steps={[
                  { 
                    label: 'Formule', 
                    value: '(Indice majoré × 4,92278€) × 25% × (Nuits travaillées ÷ 30)'
                  },
                  { 
                    label: 'Traitement mensuel', 
                    value: `${(currentIndiceMajore * 4.92278).toFixed(2)}€`,
                    calculation: `${currentIndiceMajore} × 4,92278€`
                  },
                  { 
                    label: 'Majoration de nuit', 
                    value: '25%'
                  },
                  { 
                    label: 'Nuits travaillées', 
                    value: `${prime.primeNuitConfig.nuitsParMois} nuits/mois`
                  },
                  { 
                    label: 'Prorata', 
                    value: `${((prime.primeNuitConfig.nuitsParMois / 30) * 100).toFixed(1)}%`,
                    calculation: `${prime.primeNuitConfig.nuitsParMois} ÷ 30`
                  }
                ]}
                result={{
                  label: 'Prime de nuit mensuelle',
                  value: `${prime.montantUtilisateur.toFixed(2)}€`
                }}
              />
            )}
          </div>
        );

      case 'sft':
        return (
          <div className="space-y-4">
            {prime.sftConfig && currentIndiceMajore && prime.sftConfig.nombreEnfants > 0 && (
              <PrimeCalculationExample
                title={`Calcul du SFT pour ${prime.sftConfig.nombreEnfants} enfant${prime.sftConfig.nombreEnfants > 1 ? 's' : ''}`}
                color="emerald"
                steps={[
                  { 
                    label: 'Formule', 
                    value: 'Part fixe + Part proportionnelle (à partir de 2 enfants)'
                  },
                  { 
                    label: `Part fixe (${prime.sftConfig.nombreEnfants} enfant${prime.sftConfig.nombreEnfants > 1 ? 's' : ''})`, 
                    value: (() => {
                      if (prime.sftConfig.nombreEnfants === 1) return '2,29€';
                      if (prime.sftConfig.nombreEnfants === 2) return '10,67€';
                      if (prime.sftConfig.nombreEnfants === 3) return '15,24€';
                      return `${(15.24 + (prime.sftConfig.nombreEnfants - 3) * 4.57).toFixed(2)}€`;
                    })()
                  },
                  ...(prime.sftConfig.nombreEnfants >= 2 ? [
                    { 
                      label: 'Indice plafonné', 
                      value: `${Math.min(722, Math.max(454, currentIndiceMajore))}`,
                      calculation: 'Entre 454 et 722'
                    },
                    { 
                      label: `Taux proportionnel (${prime.sftConfig.nombreEnfants} enfants)`, 
                      value: (() => {
                        if (prime.sftConfig.nombreEnfants === 2) return '3%';
                        if (prime.sftConfig.nombreEnfants === 3) return '8%';
                        return `${(8 + (prime.sftConfig.nombreEnfants - 3) * 6)}%`;
                      })()
                    }
                  ] : [])
                ]}
                result={{
                  label: 'SFT mensuel total',
                  value: `${prime.montantUtilisateur.toFixed(2)}€`
                }}
              />
            )}
          </div>
        );

      case 'isg_dom':
        return (
          <div className="space-y-4">
            <PrimeCalculationExample
              title="Calcul de l'ISG DOM"
              color="orange"
              steps={[
                { 
                  label: 'Formule', 
                  value: 'Variable selon le département et la situation familiale'
                },
                { 
                  label: 'Guadeloupe', 
                  value: 'Variable selon situation familiale'
                },
                { 
                  label: 'Guyane', 
                  value: 'Variable selon situation familiale'
                },
                { 
                  label: 'Martinique', 
                  value: 'Variable selon situation familiale'
                },
                { 
                  label: 'La Réunion', 
                  value: 'Variable selon situation familiale'
                },
                { 
                  label: 'Mayotte', 
                  value: 'Variable selon situation familiale'
                }
              ]}
              result={{
                label: 'ISG mensuelle',
                value: `${prime.montantUtilisateur.toFixed(2)}€`
              }}
            />
          </div>
        );

      case 'service':
        return (
          <div className="space-y-4">
            {currentIndiceMajore && prime.serviceTauxMax && (
              <PrimeCalculationExample
                title={`Calcul de la prime de service pour IM ${currentIndiceMajore}`}
                color="blue"
                steps={[
                  { 
                    label: 'Formule', 
                    value: 'Indice majoré × 4,92278 € × 12 × 17% ÷ 12'
                  },
                  { 
                    label: 'Traitement brut annuel', 
                    value: `${(currentIndiceMajore * 4.92278 * 12).toFixed(2)}€`,
                    calculation: `${currentIndiceMajore} × 4,92278 € × 12`
                  },
                  { 
                    label: 'Pourcentage applicable', 
                    value: `${prime.serviceTauxMax}%`
                  },
                  { 
                    label: 'Montant annuel', 
                    value: `${((currentIndiceMajore * 4.92278 * 12) * (prime.serviceTauxMax / 100)).toFixed(2)}€`,
                    calculation: `${(currentIndiceMajore * 4.92278 * 12).toFixed(2)}€ × ${prime.serviceTauxMax}%`
                  }
                ]}
                result={{
                  label: 'Maximum mensuel',
                  value: `${prime.montantDefaut.toFixed(2)}€`
                }}
              />
            )}
          </div>
        );

      case 'nbi':
        return (
          <div className="space-y-4">
            <PrimeCalculationExample
              title={`Calcul NBI avec ${prime.nbiConfig?.points || 15} points`}
              color="teal"
              steps={[
                { 
                  label: 'Formule', 
                  value: 'Nombre de points NBI × Valeur du point d\'indice'
                },
                { 
                  label: 'Points NBI', 
                  value: `${prime.nbiConfig?.points || 15} points`
                },
                { 
                  label: 'Valeur du point', 
                  value: '4,92278 €',
                  calculation: 'Valeur officielle 2024'
                }
              ]}
              result={{
                label: 'NBI mensuelle',
                value: `${prime.montantUtilisateur.toFixed(2)}€`
              }}
            />
          </div>
        );

      case 'insalubrite':
        return (
          <div className="space-y-4">
            <PrimeCalculationExample
              title="Calcul de la prime d'insalubrité catégorie 2"
              color="brown"
              steps={[
                { 
                  label: 'Formule', 
                  value: 'Montant par demi-journée × 2 × Nombre de jours travaillés par mois'
                },
                { 
                  label: 'Montant par demi-journée', 
                  value: `${(prime.montantDemiJourneeInsalubrite || 0.31).toFixed(2)}€`
                },
                { 
                  label: 'Montant par jour complet', 
                  value: `${((prime.montantDemiJourneeInsalubrite || 0.31) * 2).toFixed(2)}€`,
                  calculation: '2 demi-journées'
                },
                { 
                  label: 'Jours travaillés par mois', 
                  value: `${prime.insalubriteCat2Config?.joursTravaillesParMois || 22} jours`
                }
              ]}
              result={{
                label: 'Prime d\'insalubrité mensuelle',
                value: `${prime.montantUtilisateur.toFixed(2)}€`
              }}
            />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="mt-4 space-y-4">
      {/* Description détaillée */}
      {prime.details && (
        <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
          <p className="text-sm text-blue-800">{prime.details}</p>
        </div>
      )}

      {/* Bouton pour afficher/masquer les détails de calcul */}
      {prime.calculable && prime.calculDetails && (
        <div className="space-y-3">
          <button
            onClick={onToggleCalculationDetails}
            className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors border border-gray-200"
          >
            <div className="flex items-center gap-2">
              <Calculator className="w-4 h-4 text-gray-600" />
              <span className="text-sm font-medium text-gray-700">
                {showCalculationDetails ? 'Masquer' : 'Voir'} le détail du calcul
              </span>
            </div>
            <ChevronDown className={`w-4 h-4 text-gray-600 transition-transform ${showCalculationDetails ? 'rotate-180' : ''}`} />
          </button>
          
          {/* Détails calculables */}
          {renderPrimeDetails()}
        </div>
      )}

      {/* Encart spécial Guyane pour la prime ISG */}
      {prime.id === 'isg_dom' && (
        <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-100 rounded-xl border-2 border-green-300 shadow-lg">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <MapPin className="w-5 h-5 text-green-600" />
            </div>
            <h4 className="font-bold text-green-800 text-lg">🌴 Explore la Guyane en tant qu'IDE</h4>
          </div>
          
          <div className="mb-4">
            <h5 className="font-semibold text-green-800 mb-2">Avantages exceptionnels :</h5>
            <ul className="text-sm text-green-700 space-y-1">
              <li>• <strong>Indemnité de Sujétion Géographique (ISG)</strong>, selon conditions applicables</li>
              <li>• <strong>Prime de 40% de vie chère</strong></li>
              <li>• <strong>Billet d'avion aller Paris-Cayenne</strong></li>
              <li>• <strong>40% d'abattement sur le montant des impôts</strong> (dans la limite de 4050 euros)</li>
              <li>• <strong>Si tu es titulaire de la Fonction Publique Hospitalière</strong> : bonification de la durée de travail prise en compte pour la retraite (1 mois pour quatre réalisés)</li>
              <li>• <strong>Logement possible</strong> (colocation)</li>
            </ul>
          </div>
          
          <div className="text-center">
            <a
              href={guyaneOpportunitiesLink}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Découvrir les opportunités d'emploi IDE en Guyane - Nouvelle fenêtre"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold text-sm shadow-md hover:shadow-lg"
            >
              <MapPin className="w-4 h-4" />
              <span>Voir les offres d'emploi en Guyane</span>
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
      )}
    </div>
  );
}